'use client';
import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Alert } from '@mui/material';
import { FieldError } from 'react-hook-form';
import { cropAndCompressImage } from 'src/shared/utils/crop-and-compress-image';

interface ImageUploadProps {
  label: string;
  fieldError: FieldError | undefined;
  defaultImage?: string;
  onFileChange?: (file: File | null, fileBase64: string | null) => void;
  maxImageSize?: number;
}

const VisuallyHiddenInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => (
  <input
    ref={ref}
    {...props}
    style={{
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: 1,
      overflow: 'hidden',
      position: 'absolute',
      whiteSpace: 'nowrap',
      width: 1,
    }}
  />
));

VisuallyHiddenInput.displayName = 'VisuallyHiddenInput';

const convertFileToBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  fieldError,
  defaultImage,
  onFileChange,
}) => {
  const [image, setImage] = React.useState<{ image: string | null; error: string | null }>({
    image: defaultImage || null,
    error: null,
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImage({ image: null, error: 'No file selected' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      setImage({ image: null, error: 'Only image files are allowed' });
      return;
    }

    try {
      const processedBlob = await cropAndCompressImage(file, 10); // 10 MB

      const base64 = await convertFileToBase64(processedBlob);
      setImage({ error: null, image: base64 });
      if (onFileChange) onFileChange(file, base64);
    } catch (err) {
      setImage({ image: null, error: 'Failed to convert file to Base64' });
    }
  };

  return (
    <div className="min-w-[144px] flex flex-1 sm:flex-none flex-col items-center p-6 gap-4">
      <Avatar src={image.image || ''} sx={{ width: 80, height: 80 }} />
      <label htmlFor="image-upload">
        <Button variant="text" color="primary" size="small" component="span">
          <Typography variant="button">{label}</Typography>
        </Button>
      </label>
      <VisuallyHiddenInput
        id="image-upload"
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />
      {(image.error || fieldError?.message) && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          {fieldError?.message || image.error}
        </Alert>
      )}
    </div>
  );
};

export default ImageUpload;
